// frontend_MainDashboard/src/hooks/useActualApi.ts
// 
// Backward-compatible wrapper that uses the new domain-specific API hooks.
// This maintains the same interface as the original monolithic useActualApi
// while internally delegating to specialized hooks for better code organization.

import { useMemo } from 'react';
import {
  useChatApi,
  useContactApi,
  useTemplateApi,
  useTeamApi,
  useMediaApi,
  useCampaignApi,
  useAiApi,
  useSettingsApi,
  GetClientChatsOptions,
} from './api';

// Re-export types that were previously exported from this file
export { ApiError } from './api/useApiBase';
export { PHONEBOOK_API_CONFIG } from './api/useContactApi';
export type {
  CategoryCreatePayload,
  CategoryUpdatePayload,
  CategoryResponse,
  CategoriesResponse,
  ContactCreatePayload,
  ContactUpdatePayload,
  ContactResponse,
  ContactsResponse,
  BulkImportPayload,
  BulkImportResponse,
  ExportContactsPayload,
  ExportContactsResponse,
} from './api/useContactApi';

// Re-export GetClientChatsOptions
export type { GetClientChatsOptions };

// Combined interface matching the original useActualApi return type
export interface ActualApiHook {
  // Chat operations
  getClientChats: ReturnType<typeof useChatApi>['getClientChats'];
  getChatMessages: ReturnType<typeof useChatApi>['getChatMessages'];
  sendOperatorMessage: ReturnType<typeof useChatApi>['sendOperatorMessage'];
  sendOperatorMedia: ReturnType<typeof useChatApi>['sendOperatorMedia'];
  toggleBotPause: ReturnType<typeof useChatApi>['toggleBotPause'];
  toggleGlobalBotPause: ReturnType<typeof useChatApi>['toggleGlobalBotPause'];
  archiveChat: ReturnType<typeof useChatApi>['archiveChat'];
  deleteChat: ReturnType<typeof useChatApi>['deleteChat'];
  getArchivedChats: ReturnType<typeof useChatApi>['getArchivedChats'];
  getArchivedChatMessages: ReturnType<typeof useChatApi>['getArchivedChatMessages'];
  restoreChat: ReturnType<typeof useChatApi>['restoreChat'];
  searchChatMessages: ReturnType<typeof useChatApi>['searchChatMessages'];
  translateChatMessages: ReturnType<typeof useChatApi>['translateChatMessages'];
  
  // Settings operations
  getOperatorProfile: ReturnType<typeof useSettingsApi>['getOperatorProfile'];
  updateOperatorProfile: ReturnType<typeof useSettingsApi>['updateOperatorProfile'];
  uploadProfilePicture: ReturnType<typeof useSettingsApi>['uploadProfilePicture'];
  fetchAiPersona: ReturnType<typeof useSettingsApi>['fetchAiPersona'];
  updateAiPersona: ReturnType<typeof useSettingsApi>['updateAiPersona'];
  fetchChatbotSettings: ReturnType<typeof useSettingsApi>['fetchChatbotSettings'];
  updateChatbotSettings: ReturnType<typeof useSettingsApi>['updateChatbotSettings'];
  fetchClientUiSettings: ReturnType<typeof useSettingsApi>['fetchClientUiSettings'];
  updateClientUiSettings: ReturnType<typeof useSettingsApi>['updateClientUiSettings'];
  fetchClientConfig: ReturnType<typeof useSettingsApi>['fetchClientConfig'];
  updateClientConfig: ReturnType<typeof useSettingsApi>['updateClientConfig'];
  
  // Template operations
  createMessageTemplate: ReturnType<typeof useTemplateApi>['createMessageTemplate'];
  fetchMessageTemplates: ReturnType<typeof useTemplateApi>['fetchMessageTemplates'];
  deleteMessageTemplate: ReturnType<typeof useTemplateApi>['deleteMessageTemplate'];
  sendTemplatedMessage: ReturnType<typeof useTemplateApi>['sendTemplatedMessage'];
  
  // AI operations
  fetchAiTriggers: ReturnType<typeof useAiApi>['fetchAiTriggers'];
  createAiTrigger: ReturnType<typeof useAiApi>['createAiTrigger'];
  updateAiTrigger: ReturnType<typeof useAiApi>['updateAiTrigger'];
  deleteAiTrigger: ReturnType<typeof useAiApi>['deleteAiTrigger'];
  
  // Contact operations
  getContactCategories: ReturnType<typeof useContactApi>['getContactCategories'];
  createContactCategory: ReturnType<typeof useContactApi>['createContactCategory'];
  updateContactCategory: ReturnType<typeof useContactApi>['updateContactCategory'];
  deleteContactCategory: ReturnType<typeof useContactApi>['deleteContactCategory'];
  getPhonebookContacts: ReturnType<typeof useContactApi>['getPhonebookContacts'];
  createPhonebookContact: ReturnType<typeof useContactApi>['createPhonebookContact'];
  updatePhonebookContact: ReturnType<typeof useContactApi>['updatePhonebookContact'];
  deletePhonebookContact: ReturnType<typeof useContactApi>['deletePhonebookContact'];
  bulkImportContacts: ReturnType<typeof useContactApi>['bulkImportContacts'];
  exportContacts: ReturnType<typeof useContactApi>['exportContacts'];
  
  // Media operations
  fetchMediaLibrary: ReturnType<typeof useMediaApi>['fetchMediaLibrary'];
  uploadMedia: ReturnType<typeof useMediaApi>['uploadMedia'];
  deleteMedia: ReturnType<typeof useMediaApi>['deleteMedia'];
  renameMedia: ReturnType<typeof useMediaApi>['renameMedia'];
  moveMedia: ReturnType<typeof useMediaApi>['moveMedia'];
  
  // Team operations
  fetchTeamMembers: ReturnType<typeof useTeamApi>['fetchTeamMembers'];
  createTeamMember: ReturnType<typeof useTeamApi>['createTeamMember'];
  updateTeamMember: ReturnType<typeof useTeamApi>['updateTeamMember'];
  deleteTeamMember: ReturnType<typeof useTeamApi>['deleteTeamMember'];
  
  // Campaign operations
  createBulkCampaign: ReturnType<typeof useCampaignApi>['createBulkCampaign'];
  fetchBulkCampaigns: ReturnType<typeof useCampaignApi>['fetchBulkCampaigns'];
  getBulkCampaignDetails: ReturnType<typeof useCampaignApi>['getBulkCampaignDetails'];
  updateBulkCampaign: ReturnType<typeof useCampaignApi>['updateBulkCampaign'];
  getCampaignProgress: ReturnType<typeof useCampaignApi>['getCampaignProgress'];
}

/**
 * Main API hook that provides access to all backend endpoints.
 * 
 * This is a backward-compatible wrapper around the new domain-specific hooks.
 * Components can continue using this hook while we gradually migrate to
 * using the specialized hooks directly (useChatApi, useContactApi, etc.).
 * 
 * @example
 * ```typescript
 * const api = useActualApi();
 * const chats = await api.getClientChats();
 * ```
 */
export const useActualApi = (): ActualApiHook => {
  const chatApi = useChatApi();
  const contactApi = useContactApi();
  const templateApi = useTemplateApi();
  const teamApi = useTeamApi();
  const mediaApi = useMediaApi();
  const campaignApi = useCampaignApi();
  const aiApi = useAiApi();
  const settingsApi = useSettingsApi();

  // Memoize the combined API object to prevent unnecessary re-renders
  const api = useMemo(() => ({
    // Chat operations
    ...chatApi,
    
    // Settings operations
    ...settingsApi,
    
    // Template operations
    ...templateApi,
    
    // AI operations
    ...aiApi,
    
    // Contact operations
    ...contactApi,
    
    // Media operations
    ...mediaApi,
    
    // Team operations
    ...teamApi,
    
    // Campaign operations
    ...campaignApi,
  }), [
    chatApi,
    settingsApi,
    templateApi,
    aiApi,
    contactApi,
    mediaApi,
    teamApi,
    campaignApi,
  ]);

  return api;
};
